import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { User } from "@overmap-ai/common"
import { useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement, useCallback } from "react"

import { userFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import { UserGroup, type UserGroupProps } from "../MenuGroups/UserGroup"

interface AddUserDropdownMenuProps
	extends
		UserGroupProps,
		Omit<MenuRootProps, "children">,
		Omit<MenuClickTriggerProps, "children" | "ref">,
		MenuVariantProps {
	children: ReactElement
	onSelectAllUsers?: (users: User[]) => void
}

export const AddUserDropdownMenu = memo((props: AddUserDropdownMenuProps) => {
	const { children, users, onSelectUser, onSelectAllUsers, disabled, radius, size, variant, accentColor, ...rest } =
		props

	const [filteredUsers, filterValue, setFilterValue] = useTextFilter<User>(users, userFilterCallback)

	const handleAddAllUsers = useCallback(() => {
		onSelectAllUsers?.(filteredUsers)
	}, [onSelectAllUsers, filteredUsers])

	const isEmpty = filteredUsers.length === 0

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{!isEmpty ? <Menu.Separator /> : null}
				<Menu.Scroll>
					<UserGroup users={filteredUsers} onSelectUser={onSelectUser} />

					{onSelectAllUsers && !isEmpty ? (
						<>
							<Menu.Separator />
							<Menu.Item onSelect={handleAddAllUsers}>
								<LuIcon icon={modelIconMapping.add} />
								Add all users
							</Menu.Item>
						</>
					) : null}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AddUserDropdownMenu.displayName = "AddUserDropdownMenu"
